import { ICause, useShowCause } from 'core/Api';
import { useBreakPoints, useCrowdFundingTranslations } from 'core/Hooks';
import { Box, ButtonMantine, Container, Flex, Image, Skeleton, Space, Text } from 'ui';
import { CollapsibleCard } from 'ui/Core';

import { useDonationsCheckout } from '../../../Context';

type Props = {
  causeId?: string;
  isCardExpanded?: boolean;
  onComplete?: () => void;
};

type BodyProps = {
  causeId?: ICause['uuid'];
  onComplete?: () => void;
  isLoading: boolean;
};

const SuccessBody = ({ onComplete, causeId }: BodyProps) => {
  const { data: cause, isLoading } = useShowCause(causeId);
  const { isDesktop } = useBreakPoints();

  return (
    <Container px={0}>
      <Box style={{ display: isDesktop ? 'flex' : 'inline' }}>
        <Flex
          style={{ marginBottom: 16 }}
          justify="center"
          direction={'column'}
          maw={isDesktop ? '40%' : '100%'}
          gap="md"
        >
          <Text fw={600} fz={18}>
            Are you sure you don’t want to support{' '}
            <Skeleton visible={isLoading}>
              <Box component="span">{cause?.name}</Box>
            </Skeleton>{' '}
            every time you shop{' '}
            <Box component="span" c="cobalt">
              at no cost to you?
            </Box>
          </Text>
          <Text fz="xs">
            You could raise an additional{' '}
            <Box component="span" c="cobalt">
              $60-$100
            </Box>{' '}
            a year automatically for {cause?.name} by shopping as you normally do at over 25,000
            retailers & restaurants!
          </Text>
        </Flex>
        <Box style={{ display: 'flex', justifyContent: 'center' }}>
          <Image
            src="https://s3.amazonaws.com/com.givebacks.public/images/donations/phone-with-map.png"
            style={{ height: '207px' }}
            className="object-cover"
          />
        </Box>
      </Box>
      <Space h="lg" />
      <ButtonMantine variant="gradient" onClick={onComplete} fullWidth>
        Support us Year Round
      </ButtonMantine>
    </Container>
  );
};

export const PostDonationSuccessCard = ({ isCardExpanded, onComplete }: Props) => {
  const { useShowCart } = useDonationsCheckout();
  const { data: cart } = useShowCart();
  const causeId = cart?.cause_id;
  const { data: cause, isLoading } = useShowCause(causeId);
  const { t } = useCrowdFundingTranslations(causeId);
  // should this use subtotal rather than total because of fees/tips that will not go towards cause/campaign donation?
  const totalPrice = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'usd',
  }).format((cart?.total || 0) / 100);

  const isRecurring = cart?.recurring;
  const isPledge = cart?.status === 'pledge_required';

  return (
    <CollapsibleCard
      isCardExpanded={isCardExpanded}
      headerElement={
        <CollapsibleCard.Header
          imageUrl={cause?.thumbnail?.url}
          title={
            <Text fw={600} c="white">
              {isRecurring
                ? `Congrats! You just made your first monthly ${t(
                    'donation'
                  )} of ${totalPrice} to ${cause?.name}`
                : isPledge
                  ? `Thank you for your Pledge! Your card will be charged when the campaign has been finalized.`
                  : `Congrats! You just ${t('donated')} ${totalPrice} to ${cause?.name}`}
            </Text>
          }
          subtitle={<Text c="white">You will receive a receipt in your email</Text>}
        />
      }
    >
      <Box mt={{ base: 'md', sm: 'lg' }}>
        <SuccessBody causeId={causeId} onComplete={onComplete} isLoading={isLoading} />
      </Box>
    </CollapsibleCard>
  );
};
